import React, { useState } from 'react';
import { submitContactForm, submitSmsOptIn } from '../services/contactService';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import './Contact.css';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isValidPhone = (phone: string): boolean => {
  return phone.replace(/\D/g, '').length === 10;
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resultMessage, setResultMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const phoneValid = isValidPhone(formData.phone);
  const emailPathReady = !!(formData.email.trim() && formData.message.trim());
  const smsPathReady = phoneValid && consentChecked;
  const canSubmit = (emailPathReady || smsPathReady) && !isSubmitting;

  const getButtonText = (): string => {
    if (emailPathReady && smsPathReady) return 'Send & Subscribe';
    if (emailPathReady) return 'Send Message';
    if (smsPathReady) return 'Subscribe';
    return 'Send';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setResultMessage('');

    try {
      let recaptchaToken = '';
      if (window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute('6LdKRNkrAAAAANkzzCTY4sC_aAVZSLoioG6938j4', {
            action: 'contact_form'
          });
        } catch (error) {
          console.warn('reCAPTCHA failed, proceeding without token:', error);
        }
      }

      let emailOk = false;
      let smsOk = false;
      let smsDuplicate = false;

      const promises: Promise<void>[] = [];

      if (emailPathReady) {
        promises.push(
          submitContactForm({
            name: formData.name.trim() || 'Website Visitor',
            email: formData.email,
            message: formData.message,
            recaptchaToken
          }).then(result => {
            emailOk = result.success;
          }).catch(() => { emailOk = false; })
        );
      }

      if (smsPathReady) {
        promises.push(
          submitSmsOptIn({
            phoneNumber: formData.phone.replace(/\D/g, ''),
            recaptchaToken
          }).then(result => {
            smsOk = result.success;
            smsDuplicate = result.isDuplicate || false;
          }).catch(() => { smsOk = false; })
        );
      }

      await Promise.all(promises);

      const messages: string[] = [];
      if (emailOk) messages.push("Thanks for your message! We'll get back to you soon.");
      if (smsOk && smsDuplicate) messages.push('This phone number is already subscribed to updates.');
      else if (smsOk) messages.push("You are now subscribed to messages from TableChat. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out. Reply HELP for help.");

      if (!emailOk && emailPathReady) messages.push('There was an issue sending your message.');
      if (!smsOk && smsPathReady && !smsDuplicate) messages.push('There was an issue with the SMS signup.');

      if (emailOk || smsOk) {
        setSubmitStatus('success');
        setResultMessage(messages.join(' '));
        if (emailOk) setFormData(prev => ({ ...prev, name: '', email: '', message: '' }));
        if (smsOk) {
          setFormData(prev => ({ ...prev, phone: '' }));
          setConsentChecked(false);
        }
      } else {
        setSubmitStatus('error');
        setResultMessage(messages.join(' ') || 'Something went wrong. Please try again or email us directly.');
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      setSubmitStatus('error');
      setResultMessage('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us - TableChat | Get in Touch"
        description="Contact the TableChat support team. Questions about setup, pricing, or features? We're here to help. Sign up for SMS product updates."
        canonical="https://tablechat.io/contact"
      />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Contact', url: 'https://tablechat.io/contact' },
      ])} />
      <div className="container">
        <div className="contact-header">
          <h1>Contact Us</h1>
          <p>Reach out by email for help or questions. Sign up by phone to see what's new in messaging for your customers!</p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="info-section">
              <h3>Get in Touch</h3>
              <p>Ready to get started or have questions about TableChat? Reach out to our team.</p>
            </div>

            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="method-info">
                  <h4>Email Support</h4>
                  <p>Get help with setup, billing, or technical issues</p>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22 20.48 21.56 20.93 21 20.97C20.64 21 20.27 21.02 19.9 21.02C10.96 21.02 3.65 14.2 3.03 5.35C3 4.78 3.45 4.29 4.02 4.25C4.26 4.23 4.5 4.22 4.74 4.22H7.74C8.28 4.22 8.74 4.6 8.84 5.13C8.97 5.82 9.18 6.49 9.47 7.13L7.86 8.74C9.27 11.32 11.37 13.42 13.95 14.83L15.56 13.22C16.2 13.51 16.87 13.72 17.56 13.85C18.1 13.95 18.47 14.41 18.47 14.95V17.95" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="method-info">
                  <h4>SMS Product Updates</h4>
                  <p>Sign up to hear about new features and offerings</p>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 6V12L16 14" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="method-info">
                  <h4>Response Time</h4>
                  <p>We typically respond within 24 hours</p>
                  <span>Monday - Friday, 9 AM - 6 PM EST</span>
                </div>
              </div>
            </div>

          </div>

          <div className="contact-form-section">
            <form className="contact-form" onSubmit={handleSubmit}>
              <h3>Get in Touch</h3>
              
              {submitStatus === 'success' && (
                <div className="form-message success">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {resultMessage}
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="form-message error">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#F44336" strokeWidth="2"/>
                    <line x1="15" y1="9" x2="9" y2="15" stroke="#F44336" strokeWidth="2"/>
                    <line x1="9" y1="9" x2="15" y2="15" stroke="#F44336" strokeWidth="2"/>
                  </svg>
                  {resultMessage}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    rows={5}
                    placeholder="Tell us how we can help you..."
                  />
                </div>
              </div>

              <div className="form-divider">
                <span>SMS Updates</span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">SMS — New Product Updates</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    placeholder="(555) 123-4567"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className={`consent-section ${phoneValid ? 'visible' : ''}`}>
                <label className="consent-label">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    disabled={isSubmitting}
                    className="consent-checkbox"
                  />
                  <span className="consent-text">
                    By checking this box and providing my phone number, I agree to receive
                    promotional and marketing messages from TableChat, including product updates,
                    feature announcements, and offers. Messages are sent using automated technology.
                    Consent is not required as a condition of purchase. Message frequency varies.
                    Message and data rates may apply. Reply STOP to unsubscribe or HELP for help. View{' '}
                    <a href="https://tablechat.io/messaging-policy" target="_blank" rel="noopener noreferrer">
                      Messaging Policy
                    </a>
                    {' and '}
                    <a href="https://tablechat.io/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>.
                  </span>
                </label>
                {phoneValid && !consentChecked && (
                  <p className="consent-hint">Check the box above to subscribe to SMS updates</p>
                )}
              </div>

              <div className="form-row">
                <button 
                  type="submit" 
                  className={`btn btn-primary btn-large ${smsPathReady && !emailPathReady ? 'btn-subscribe' : ''}`}
                  disabled={!canSubmit}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    getButtonText()
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
